import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { PlainButton, StatStrip, StatTile } from 'components/v3';
import { shortDate } from '../../Forms/Submissions/StatsRow';
import { keepThrough } from '../shared/taxYear';

// Four KPI tiles (design W9Parts.Stats, w9-plan §6.3 item 4). Which four
// depends on the view: the current year, an archived year, or all years.
// The Not back tile is a button that toggles the table's ?filter=notback.

const num = (n) => Number(n || 0).toLocaleString();

const latest = (stats) => ({
  value:
    stats && stats.latest_submitted_at
      ? shortDate(stats.latest_submitted_at)
      : '—',
  caption:
    stats && stats.latest_submitted_at
      ? stats.latest_submitter_name || ' '
      : 'none yet',
});

// → [{ key, label, value, caption, clickable }]
export const tilesFor = ({ mode, year, stats, hasPrior, allStats }) => {
  if (mode === 'all') {
    const a = allStats || {};
    return [
      {
        key: 'drivers',
        label: 'Drivers on file',
        value: num(a.drivers),
        caption: 'a signed W-9 in any year',
      },
      {
        key: 'current',
        label: 'Current',
        value: num(a.current),
        caption: `signed for ${year}`,
      },
      {
        key: 'earlier',
        label: 'Earlier year only',
        value: num(a.earlier),
        caption: `latest W-9 is from before ${year}`,
      },
      {
        key: 'stored',
        label: 'W-9s stored',
        value: num(a.stored),
        caption: `across ${num(a.yearCount)} tax ${
          a.yearCount === 1 ? 'year' : 'years'
        }`,
      },
    ];
  }
  const dash = (v) => (stats ? v : '—');
  const last = latest(stats);
  if (mode === 'archived') {
    return [
      {
        key: 'onfile',
        label: 'On file',
        value: dash(num(stats && stats.on_file)),
        caption: 'final count',
      },
      {
        key: 'businesses',
        label: 'Businesses',
        value: dash(num(stats && stats.businesses)),
        caption: stats ? `${num(stats.individuals)} individuals (SSN)` : null,
      },
      {
        key: 'keep',
        label: 'Keep through',
        value: String(keepThrough(year)),
        caption: 'IRS 4-year guidance',
      },
      {
        key: 'last',
        label: 'Last W-9',
        value: last.value,
        caption: stats ? last.caption : null,
      },
    ];
  }
  const month = stats ? stats.this_month : 0;
  return [
    {
      key: 'onfile',
      label: 'On file',
      value: dash(num(stats && stats.on_file)),
      caption: stats
        ? month
          ? `+${num(month)} this month`
          : 'none yet this month'
        : null,
    },
    {
      key: 'week',
      label: 'New this week',
      value: dash(num(stats && stats.new_this_week)),
      caption: 'in the last 7 days',
    },
    hasPrior
      ? {
          key: 'notback',
          label: 'Not back',
          value: dash(num(stats && stats.not_back)),
          caption: `filed in ${year - 1}`,
          clickable: true,
        }
      : {
          key: 'notback',
          label: 'Not back',
          value: '—',
          caption: `no ${year - 1} list`,
        },
    {
      key: 'latest',
      label: 'Latest W-9',
      value: last.value,
      caption: stats ? last.caption : null,
    },
  ];
};

const TileButton = styled(PlainButton)`
  display: block;
  width: 100%;
  text-align: left;
  border-radius: 16px;

  &:hover > div {
    border-color: rgba(250, 70, 22, 0.4);
  }
`;

const W9Stats = ({
  mode,
  year,
  stats,
  loading,
  hasPrior,
  allStats,
  activeFilter,
  onToggleNotBack,
  isMobile,
}) => {
  const tiles = tilesFor({ mode, year, stats, hasPrior, allStats });
  return (
    <div data-w9-stats={mode}>
      <StatStrip minWidth={isMobile ? 150 : 190}>
        {tiles.map((tile) => {
          const node = (
            <StatTile
              label={tile.label}
              value={tile.value}
              caption={tile.caption}
              loading={loading && tile.key !== 'keep'}
              accent={tile.clickable && activeFilter === 'notback'}
            />
          );
          if (!tile.clickable) return <div key={tile.key}>{node}</div>;
          const on = activeFilter === 'notback';
          return (
            <TileButton
              key={tile.key}
              aria-pressed={on}
              title="Show who hasn't sent this year's W-9 in the table"
              onClick={onToggleNotBack}
            >
              {node}
            </TileButton>
          );
        })}
      </StatStrip>
    </div>
  );
};

W9Stats.propTypes = {
  mode: PropTypes.oneOf(['current', 'archived', 'all']).isRequired,
  // The tax year (the current one on All years).
  year: PropTypes.number.isRequired,
  stats: PropTypes.shape({
    on_file: PropTypes.number,
    this_month: PropTypes.number,
    new_this_week: PropTypes.number,
    not_back: PropTypes.number,
    businesses: PropTypes.number,
    individuals: PropTypes.number,
    latest_submitted_at: PropTypes.string,
    latest_submitter_name: PropTypes.string,
  }),
  loading: PropTypes.bool,
  hasPrior: PropTypes.bool,
  allStats: PropTypes.shape({
    drivers: PropTypes.number,
    current: PropTypes.number,
    earlier: PropTypes.number,
    stored: PropTypes.number,
    yearCount: PropTypes.number,
  }),
  activeFilter: PropTypes.string,
  onToggleNotBack: PropTypes.func,
  isMobile: PropTypes.bool,
};

W9Stats.defaultProps = {
  stats: null,
  loading: false,
  hasPrior: false,
  allStats: null,
  activeFilter: null,
  onToggleNotBack: undefined,
  isMobile: false,
};

export default W9Stats;
